<template>
  <div class="phone-input-container global-relative">
    <vue-tel-input
      ref="phoneInput"
      :model-value="modelValue"
      v-bind="options"
      :disabled="disabled"
      :only-countries="onlyCountries"
      :ignored-countries="ignoredCountries"
      :preferred-countries="preferredCountries"
      @on-input="(formattedNumber, phoneObject)=>onInput(formattedNumber, phoneObject)"
      @country-changed="country = $event"
      @focus="onFocus()"
      @blur="onBlur()"
    />
  </div>
</template>

<script>
  import 'vue-tel-input/vue-tel-input.css'
  import { VueTelInput } from 'vue-tel-input'

  const ERROR_MIN_LENGTH = 4

  export default {
    name: 'PhoneInput',
    components: {
      VueTelInput
    },
    props: {
      modelValue: {
        type: String,
        default: ''
      },
      onlyCountries: {
        type: Array,
        default: () => []
      },
      ignoredCountries: {
        type: Array,
        default: () => []
      },
      preferredCountries: {
        type: Array,
        default: () => []
      },
      disabled: {
        type: Boolean,
        default: false
      }
    },
    emits: ['input', 'set-error'],
    data() {
      return {
        isValid: false,
        isFocused: false,
        country: {
          areaCode: null,
          dialCode: '',
          iso2: '',
          name: '',
          priority: 0
        },
        options: {
          inputOptions: {
            showDialCode: true
          },
          name: 'telephone',
          autoFormat: true,
          dropdownOptions: {
            showSearchBox: true,
            showFlags: true,
            showDialCodeInList: true,
            showDialCodeInSelection: false
          },
          mode: 'international',
          validCharactersOnly: true
        }
      }
    },
    computed: {
      isError() {
        return (
          !this.isValid
          && !this.isFocused
          && this.modelValue.length > this.country.dialCode.length + 1
          && this.modelValue.length > ERROR_MIN_LENGTH
        )
      }
    },
    watch: {
      isError(value) {
        if (value) {
          this.$emit('set-error', { phone: this.$t('main.phone_input_validation_failed') })
        }
      }
    },
    methods: {
      onFocus() {
        this.isFocused = true
      },
      onBlur() {
        this.isFocused = false

        this.$nextTick(() => {
          if (this.modelValue && !this.modelValue.startsWith('+')) {
            this.$emit('input', `+${this.modelValue}`)
          }
        })
      },
      onInput(formattedNumber, phoneObject) {
        if (this.$refs.phoneInput?.cleanInvalidCharacters) {
          this.$refs.phoneInput.cleanInvalidCharacters()
        }

        if (!formattedNumber.startsWith('+')) {
          formattedNumber = `+${formattedNumber}`
        }

        this.isValid = phoneObject?.valid ?? false

        this.$emit('input', formattedNumber)
      }
    }
  }
</script>

<style scoped lang="scss">
  .phone-input-container {
    & .vue-tel-input {
      height: 46px;
      background-color: transparent;
      border: unset;

      &:hover {
        border-color: rgb(var(--v-theme-primary));
      }

      &:focus,
      &:focus-within,
      &:focus-visible {
        box-shadow: none;
      }

      & :deep(.vti__dropdown-list) {
        padding-left: 0 !important;
        width: 300px;
        overflow-x: hidden;
      }

      & :deep(.vti__dropdown) {
        border-right: 1px solid rgb(var(--v-theme-secondary-200));
      }

      & :deep(.vti__search_box) {
        width: 100%;
        margin: 0;
        margin-bottom: 2px;
        border: none;
        border-bottom: 1px solid rgba(0, 0, 0, 0.38);
      }

      & :deep(.vti__dropdown-item.highlighted) {
        position: relative;
        background-color: transparent;
        color: rgb(var(--v-theme-primary));

        &::before {
          position: absolute;
          content: '';
          width: 100%;
          height: 100%;
          background-color: rgb(var(--v-theme-primary));
          opacity: 0.16;
          top: 0;
          left: 0;
        }
      }
    }
  }
</style>
